import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";

const LanguageSwitcher = ({ variant = "desktop" }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = (i18n.language || "en").startsWith("es") ? "es" : "en";
  const label = current === "es" ? "ES" : "EN";

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  const isMobile = variant === "mobile";

  return (
    <div ref={ref} style={isMobile ? styles.mobileWrap : styles.wrap} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={isMobile ? styles.mobileButton : styles.button}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-button"
      >
        <Globe size={16} strokeWidth={1.75} />
        <span style={styles.label}>{label}</span>
        <ChevronDown size={14} strokeWidth={2} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s" }} />
      </button>
      {open && (
        <div role="listbox" style={isMobile ? styles.mobileMenu : styles.menu} data-testid="language-switcher-menu">
          <button
            role="option"
            aria-selected={current === "es"}
            onClick={() => choose("es")}
            style={{ ...styles.item, ...(current === "es" ? styles.itemActive : {}) }}
            data-testid="language-switcher-item-es"
          >
            {t("langSwitcher.es")}
          </button>
          <button
            role="option"
            aria-selected={current === "en"}
            onClick={() => choose("en")}
            style={{ ...styles.item, ...(current === "en" ? styles.itemActive : {}) }}
            data-testid="language-switcher-item-en"
          >
            {t("langSwitcher.en")}
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { position: "relative" },
  mobileWrap: { position: "relative", marginTop: "8px" },
  button: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "transparent",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: "999px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#4a5568",
    cursor: "pointer",
    transition: "border-color 0.15s, color 0.15s, background 0.15s"
  },
  mobileButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    background: "transparent",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#4a5568",
    cursor: "pointer"
  },
  label: { letterSpacing: "0.06em" },
  menu: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    minWidth: "140px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
    padding: "6px",
    zIndex: 1100,
    display: "flex",
    flexDirection: "column"
  },
  mobileMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
    padding: "6px",
    zIndex: 1100,
    display: "flex",
    flexDirection: "column"
  },
  item: {
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#2d3748",
    borderRadius: "8px",
    cursor: "pointer"
  },
  itemActive: {
    background: "linear-gradient(135deg, rgba(102,126,234,0.10) 0%, rgba(118,75,162,0.10) 100%)",
    color: "#667eea",
    fontWeight: 700
  }
};

export default LanguageSwitcher;
